// Copyright (c) Ping. All rights reserved.
//
// Builds + dispatches a `com.ping.gif` envelope through an MLS
// `Conversation` handle and writes a local-echo row so the
// sender's bubble renders immediately. Envelope shape mirrors the
// voice envelope but carries a URL (Tenor CDN) instead of inline
// bytes — GIFs are typically too large to base64 the full payload
// over MLS and the public CDN is fast enough to render in line.
//
//   { kind: 'com.ping.gif',
//     payload: {
//       url:    'https://media.tenor.com/...',
//       width:  240,
//       height: 180,
//       altText: '…'   // optional
//     } }
//
// The receiver-side decoder (shared
// `packages/ui/src/sync/useIncomingMessages.ts`) persists the
// FULL envelope JSON as the row's `body` for `com.ping.gif` kinds
// so the chat surface's `messageVariants` map can dispatch the
// right bubble.

import {
  encodeEnvelopeJsonBytes,
  encodeEnvelopeJsonString,
  type GifItem,
  type Message,
  mintEventId,
  persistSentMessage as sharedPersistSentMessage,
} from '@ping/ui';

import { log } from '@core/log/log';
import { retrieveIdentity } from '@core/persistence/identity';

import { webConversationsPersister, webMessagesPersister } from './persistenceAdapters';

interface SentMessageEnvelope {
  conversation_id: Uint8Array;
  epoch: number | bigint;
  content_hash: Uint8Array;
  hlc: { wall_ms: number | bigint; logical: number };
}

interface SendableConversation {
  send(plaintext: Uint8Array): Promise<SentMessageEnvelope>;
}

export interface GifEnvelopePayload {
  readonly url: string;
  readonly width: number;
  readonly height: number;
  readonly altText?: string | undefined;
  /**
   * True when this `com.ping.gif` envelope is a STICKER rather than a GIF.
   * Additive + backward-compatible: older clients ignore it and render the
   * item as a normal GIF tile. The chat surface uses it to render stickers
   * with no bubble/background chrome — just the image. Sourced from the
   * Stickers tab (`onSendSticker`) vs the GIFs tab (`onSendGif`).
   */
  readonly sticker?: boolean | undefined;
}

export interface GifEnvelope {
  readonly kind: 'com.ping.gif';
  readonly payload: GifEnvelopePayload;
  /** Thread root content-hash when sent as a reply into a thread. */
  readonly parent_id?: string;
}

export interface SendGifMessageOptions {
  readonly conv: SendableConversation;
  readonly conversationId: string;
  readonly gif: GifItem;
  /** When set, posts the GIF as a reply into this thread root. */
  readonly parentId?: string | undefined;
  /** When true, tags the envelope as a sticker (`payload.sticker`) so the
   *  receiver + the sender's own echo render it without bubble chrome. */
  readonly isSticker?: boolean | undefined;
}

/**
 * Encodes the selected `GifItem` as a `com.ping.gif` envelope,
 * sends it through MLS, and writes the local-echo row so the
 * sender's bubble renders immediately. Returns the resolved
 * content-hash hex on success.
 */
export async function sendGifMessage(opts: SendGifMessageOptions): Promise<string> {
  const { conv, conversationId, gif, parentId, isSticker } = opts;

  const payload: GifEnvelopePayload = {
    url: gif.fullUrl,
    width: gif.width,
    height: gif.height,
    ...(typeof gif.altText === 'string' && gif.altText.length > 0 ? { altText: gif.altText } : {}),
    ...(isSticker === true ? { sticker: true } : {}),
  };
  const eventId = mintEventId();
  const encodable = {
    kind: 'com.ping.gif' as const,
    event_id: eventId,
    // Thread reply (GIF or sticker) → self-identify with `payload.thread: true`
    // so the main timeline folds it out (parent_id alone also marks an inline
    // reply-quote). For non-text kinds parent_id only ever means a thread reply.
    payload: { ...payload, ...(parentId !== undefined ? { thread: true } : {}) },
    ...(parentId !== undefined ? { parent_id: parentId } : {}),
  };
  const payloadBody = encodeEnvelopeJsonString(encodable);
  const payloadBytes = encodeEnvelopeJsonBytes(encodable);

  const sentEnvelope = await conv.send(payloadBytes);
  const contentHashHex = bytesToHex(sentEnvelope.content_hash);

  const identity = await retrieveIdentity();
  if (!identity) {
    log.warn({ event: 'gif.persistSent.no_identity' });
    return contentHashHex;
  }

  const epoch =
    typeof sentEnvelope.epoch === 'bigint' ? Number(sentEnvelope.epoch) : sentEnvelope.epoch;
  const sentAt = Date.now();
  const id = eventId;

  await sharedPersistSentMessage({
    messages: webMessagesPersister,
    conversations: webConversationsPersister,
    record: {
      id,
      conversationId,
      epoch,
      serverSeq: sentAt,
      contentHash: contentHashHex,
      senderAccountId: identity.accountId,
      // Persist the full envelope JSON as the body so the
      // chat surface can parse it client-side and dispatch
      // the gif variant. Body is opaque-string in the shared
      // schema.
      body: payloadBody,
      sentAt,
      deliveryState: 'sent',
      ...(parentId !== undefined
        ? { inReplyTo: { messageId: parentId, preview: 'GIF' } }
        : {}),
    },
  });

  log.debug({
    event: 'gif.send.ok',
    conversationIdPrefix: conversationId.slice(0, 6),
    gifId: gif.id,
    url: gif.fullUrl,
  });

  return contentHashHex;
}

/** Detects whether a persisted `Message.body` is a GIF envelope. */
export function isGifEnvelopeBody(body: string | undefined): boolean {
  if (body === undefined || body.length === 0) return false;
  if (!body.startsWith('{')) return false;
  return body.includes('"com.ping.gif"');
}

/**
 * Parses a `com.ping.gif` envelope from a persisted message body.
 * Returns `undefined` on any decode failure so the caller can fall
 * back to the plain-text bubble.
 */
export function parseGifEnvelope(body: string | undefined): GifEnvelopePayload | undefined {
  if (body === undefined) return undefined;
  try {
    const parsed = JSON.parse(body) as Partial<GifEnvelope>;
    if (parsed.kind !== 'com.ping.gif') return undefined;
    const p = parsed.payload;
    if (
      p === undefined ||
      typeof p.url !== 'string' ||
      typeof p.width !== 'number' ||
      typeof p.height !== 'number'
    ) {
      return undefined;
    }
    return {
      url: p.url,
      width: p.width,
      height: p.height,
      ...(typeof p.altText === 'string' ? { altText: p.altText } : {}),
      ...(p.sticker === true ? { sticker: true } : {}),
    };
  } catch {
    return undefined;
  }
}

// Local copy — the persistence layer doesn't expose this helper.
function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}

// Re-export the Message type as `_Message` so this file's `import`
// graph is non-empty even when only `parseGifEnvelope` is consumed
// (helps the bundler tree-shake without breaking type imports).
export type { Message as _Message };
